import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import {
  border,
  color,
  compose,
  flexbox,
  layout,
  position,
  space,
  system,
} from 'styled-system';

import { forwardValidProp } from '../../../utils/style';

const baseCell = ({ flexDirection, flexWrap, justifySelf }) => css`
  display: inline-flex;
  flex-direction: ${flexDirection};
  flex-wrap: ${flexWrap};
  justify-self: ${justifySelf};
  min-width: 0;
  width: 100%;
`;

const StyledCell = styled('div', {
  shouldForwardProp: (prop) => forwardValidProp(prop),
})`
  ${baseCell}
  ${system({
    gridColumnEnd: { property: 'gridColumnEnd' },
    gridColumnStart: { property: 'gridColumnStart' },
    gridRowEnd: { property: 'gridRowEnd' },
    gridRowStart: { property: 'gridRowStart' },
  })}
  ${compose(border, color, flexbox, layout, space, position)}
`;

const Cell = forwardRef(({ ...props }, ref) => (
  <StyledCell ref={ref} {...props} />
));

Cell.displayName = 'Cell';

Cell.defaultProps = {
  gridColumnEnd: 'span 1',
  gridColumnStart: null,
  gridRowEnd: null,
  gridRowStart: null,
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifySelf: 'stretch',
};

Cell.propTypes = {
  /**
   * `grid-column-end` css prop
   */
  gridColumnEnd: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  /**
   * `grid-column-start` css prop
   */
  gridColumnStart: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  /**
   * `grid-row-end` css prop
   */
  gridRowEnd: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  /**
   * `grid-row-start` css prop
   */
  gridRowStart: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  /**
   * `flex-direction` css prop
   */
  flexDirection: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * `flex-wrap` css prop
   */
  flexWrap: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * `justify-self` css prop
   */
  justifySelf: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  /**
   * Implements `styled-system` [color](https://styled-system.com/api/#color) utility
   */
  ...propTypes.color,
  /**
   * Implements `styled-system` [flexbox](https://styled-system.com/api/#flexbox) utility
   */
  ...propTypes.flexbox,
  /**
   * Implements `styled-system` [layout](https://styled-system.com/api/#layout) utility
   */
  ...propTypes.layout,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
};

export default Cell;
